import { useState, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { useUserSearch } from "@/graphql/hooks/useUserSearch"
import {
  TournamentPlayerRegisterInput,
  useTournamentPlayerRegister,
} from "@/graphql/hooks/useTournamentPlayerRegister"
import { useToast } from "@/hooks/use-toast"
import { BanIcon, Check, CheckCircle, Search, TriangleAlert, UserPlus } from "lucide-react"
import { Badge } from "./ui/badge"
import { validateRUT, formatRUT } from "@/lib/rutValidation"

interface Person {
  id: number
  code: string
  email: string
  name: string
  fullName: string
  requireDocument: boolean
  documentNumber: string
  documentNumberType: number
}

interface Race {
  id: number
  raceId: number
  sortOrder: number
  Race?: {
    name: string
  }
}

interface Tournament {
  id: number
  Format?: {
    id?: number
    name?: string
    description?: string
    isFullRacial?: number
  }
  Game?: {
    RaceGames?: Race[]
  }
}

interface PlayerSearchFormProps {
  tournament: Tournament
  players: Array<{ personId: number }>
  onPlayerRegistered: () => void
}

export const PlayerSearchForm = ({
  tournament,
  onPlayerRegistered,
  players,
}: PlayerSearchFormProps) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedPersonId, setSelectedPersonId] = useState<string>("")
  const [selectedRaceId, setSelectedRaceId] = useState<string>("")
  const [searchResults, setSearchResults] = useState<Person[]>([])
  const [searchActive, setSearchActive] = useState(false)
  const [documentType, setDocumentType] = useState<string>("RUT")
  const [documentNumber, setDocumentNumber] = useState<string>("")
  const [documentError, setDocumentError] = useState<string>("")

  const { searchForUsers, loading: searchLoading } = useUserSearch()
  const { registerTournamentPlayer, loading: registerLoading } = useTournamentPlayerRegister()
  const { toast } = useToast()

  const races = useMemo(() => {
    const razas = tournament.Game?.RaceGames || []
    return [...razas].sort((a, b) => a.sortOrder - b.sortOrder)
  }, [tournament.Game.RaceGames])

  const enrolledPlayerIds = players?.map((tp) => tp.personId) || []

  const selectedPerson = searchResults.find((p) => p.id.toString() === selectedPersonId)
  const requiresDocument = selectedPerson && !selectedPerson.requireDocument

  const validateDocument = (type: string, number: string): string => {
    if (!number.trim()) return "El número de documento es requerido"

    if (type === "RUT") {
      if (!validateRUT(number)) {
        return "RUT inválido"
      }
    } else {
      // Para CC, CI, DNI - validar que no tenga espacios ni caracteres especiales
      if (!/^[a-zA-Z0-9]+$/.test(number)) {
        return "El documento solo puede contener letras y números"
      }
    }
    return ""
  }

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!searchTerm.trim()) return

    try {
      const result = await searchForUsers({
        search: searchTerm,
        excludes: enrolledPlayerIds,
        tournamentId: tournament.id,
        organizer: false,
      })

      if (result?.status?.success) {
        const persons = result.Persons.map((x) => {
          return {
            ...x,
            requireDocument: !!x.documentNumber,
          }
        })
        setSearchResults(persons || [])
      } else {
        toast({
          title: "Error en la búsqueda",
          description: result?.status?.detail || "No se pudo realizar la búsqueda",
          variant: "destructive",
        })
      }
      setSearchActive(true)
    } catch (error) {
      toast({
        title: "Error en la búsqueda",
        description: "No se pudo realizar la búsqueda de jugadores",
        variant: "destructive",
      })
    }
  }

  const handleRegister = async () => {
    if (!selectedPersonId) return

    // Validar documentos si son requeridos
    if (requiresDocument) {
      if (!documentType || !documentNumber) {
        setDocumentError("Tipo y número de documento son requeridos")
        return
      }

      const error = validateDocument(documentType, documentNumber)
      if (error) {
        setDocumentError(error)
        return
      }
    }

    const registerData: TournamentPlayerRegisterInput = {
      tournamentId: tournament.id,
      personId: parseInt(selectedPersonId),
    }

    if (races.length > 0 && selectedRaceId) {
      registerData.raceId = parseInt(selectedRaceId)
    }

    if (requiresDocument && documentType && documentNumber) {
      registerData.documentType = documentType
      registerData.documentNumber =
        documentType === "RUT" ? formatRUT(documentNumber) : documentNumber
    }

    try {
      const result = await registerTournamentPlayer(registerData)

      if (result?.status?.success) {
        const player = result.TournamentPlayer
        toast({
          title: "Jugador inscrito exitosamente",
          description: `${player.Person.fullName} (${player.Person.code}) ha sido inscrito al torneo`,
          variant: "success",
        })

        // Reset form
        setSearchTerm("")
        setSelectedPersonId("")
        setSelectedRaceId("")
        setSearchResults([])
        setDocumentType("RUT")
        setDocumentNumber("")
        setDocumentError("")

        // Notify parent to refresh player list
        onPlayerRegistered()
      } else {
        toast({
          title: "Error al inscribir jugador",
          description: result?.status?.detail || "No se pudo inscribir al jugador",
          variant: "destructive",
        })
      }
      setSearchActive(false)
    } catch (error) {
      toast({
        title: "Error al inscribir jugador",
        description: "No se pudo inscribir al jugador en el torneo",
        variant: "destructive",
      })
    }
  }

  const canRegister =
    selectedPersonId &&
    // Si el torneo es racial, debe seleccionar raza
    (tournament.Format?.isFullRacial ? !!selectedRaceId : true) &&
    (!requiresDocument || (documentType && documentNumber))

  return (
    <Card className="bg-white dark:bg-gray-800 shadow-md rounded-xl">
      <CardHeader>
        <CardTitle className="text-gray-900 dark:text-gray-100">
          Buscar e Inscribir Jugador
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Search Form */}
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="search" className="text-gray-900 dark:text-gray-100">
              Buscar jugador
            </Label>
            <div className="flex gap-2">
              <Input
                id="search"
                placeholder="Nombre, código o email del jugador"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={searchLoading}
                className="dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
              />
              <Button type="submit" disabled={searchLoading || !searchTerm.trim()}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </form>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="player-select" className="text-gray-900 dark:text-gray-100">
                Seleccionar jugador
              </Label>
              <Select value={selectedPersonId} onValueChange={setSelectedPersonId}>
                <SelectTrigger className="dark:bg-gray-700 dark:text-gray-100">
                  <SelectValue placeholder="Selecciona un jugador" />
                </SelectTrigger>
                <SelectContent className="dark:bg-gray-700 dark:text-gray-100">
                  {searchResults.map((person) => (
                    <SelectItem
                      key={person.id}
                      value={person.id.toString()}
                      className="dark:text-gray-100"
                    >
                      {person.requireDocument ? (
                        <CheckCircle className="inline text-green-600 h-4" />
                      ) : (
                        <TriangleAlert className="inline text-yellow-500 h-4" />
                      )}
                      {person.fullName} ({person.code}) - {person.email}
                      {!person.requireDocument && (
                        <Badge variant="info" className="mx-2 dark:bg-blue-700 dark:text-gray-100">
                          Requiere identificación
                        </Badge>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Document Selection for users without documents */}
            {requiresDocument && (
              <div className="space-y-4 p-4 border rounded-lg bg-yellow-50 dark:bg-yellow-950/20">
                <div className="flex items-center gap-2">
                  <TriangleAlert className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Este usuario requiere verificación de documento
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="document-type" className="text-gray-900 dark:text-gray-100">
                      Tipo de Documento
                    </Label>
                    <Select
                      value={documentType}
                      onValueChange={(value) => {
                        setDocumentType(value)
                        setDocumentError("")
                      }}
                    >
                      <SelectTrigger className="dark:bg-gray-700 dark:text-gray-100">
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-gray-700 dark:text-gray-100">
                        <SelectItem value="RUT">RUT (Chile)</SelectItem>
                        <SelectItem value="CC">Cédula de Ciudadanía (CC)</SelectItem>
                        <SelectItem value="CI">Cédula de Identidad (CI)</SelectItem>
                        <SelectItem value="DNI">DNI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="document-number" className="text-gray-900 dark:text-gray-100">
                      Número de Documento
                    </Label>
                    <Input
                      id="document-number"
                      placeholder={documentType === "RUT" ? "12345678-9" : "Número del documento"}
                      value={documentNumber}
                      onChange={(e) => {
                        const value = e.target.value
                        setDocumentNumber(value)
                        setDocumentError("")
                      }}
                      className="dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                    />
                  </div>
                </div>

                {documentError && (
                  <p className="text-md text-red-600 dark:text-red-400">{documentError}</p>
                )}
              </div>
            )}

            {/* Race Selection */}
            {tournament.Format.isFullRacial && races.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="race-select" className="text-gray-900 dark:text-gray-100">
                  Seleccionar raza
                </Label>
                <Select value={selectedRaceId} onValueChange={setSelectedRaceId}>
                  <SelectTrigger className="dark:bg-gray-700 dark:text-gray-100">
                    <SelectValue placeholder="Selecciona una raza" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-gray-700 dark:text-gray-100">
                    {races.map((race) => (
                      <SelectItem
                        key={race.raceId}
                        value={race.raceId?.toString()}
                        className="dark:text-gray-100"
                      >
                        {race.Race.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Register Button */}
            <Button
              onClick={handleRegister}
              disabled={!canRegister || registerLoading}
              className="w-full"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {registerLoading ? "Inscribiendo..." : "Inscribir Jugador"}
            </Button>
          </div>
        )}

        {searchResults.length === 0 && searchTerm && !searchLoading && searchActive && (
          <p className="text-center text-muted-foreground dark:text-gray-300 py-4">
            No se encontraron jugadores para "{searchTerm}"
          </p>
        )}
      </CardContent>
    </Card>
  )
}
